// Receiving (WP4, spec §4.2.2, R7.13a–c) in the design language v1 (docs/design/mockups/
// receiving-v1): pick the purchase order, then run RECEIVE_PALLET pallet by pallet — scan
// the pallet or have an SSCC issued, scan every container, end the pallet — straight into
// the next pallet, and close the delivery with a reason per difference. The interpreter
// decides; this screen resolves scans, keeps the drafts and sends the step batch.

import { GS1ParseError, RECEIVE_PALLET, applyInput, parseScan, startRun, type Effect, type GS1Elements, type RunState } from "@trail/core";
import { canManual } from "../pick/Pick.js";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Api, effectiveSettings as getSettings, getPrefs, savePrefs, type ExpectedReceiptDetail, type ExpectedReceiptSummary, type ReceiptProgress, type Qty } from "../api/client.js";
import { refreshOrderNow, useErpRuns } from "../api/erp-refresh.js";
import { CameraView } from "../CameraView.js";
import { fmtNum, fmtQty, t, uomLabel } from "../i18n.js";
import { isGs1Symbology, type CodeState } from "../scan/camera.js";
import { createAssembler } from "../scan/assemble.js";
import { feedback, unlockAudio } from "../scan/feedback.js";
import { attachWedge } from "../scan/wedge.js";
import { Actions, Banner, Btn, Chips, Entry, Glance, LabelStrip, ListItem, Prompt, Rows, ScanBar, Sheet, Strip, focusScan, type BannerData, type LabelCell, type RowData } from "../ui/components.jsx";
import { asValue, buildStepBatch, tally, type ScanRecord } from "./events.js";
import { discard, pending, startQueueWorker, submitStep, subscribe, type QueuedStep } from "./queue.js";
import { reasonsForExtra, reasonsForShort, type ReasonCode } from "./reasons.js";
import { isBulkProduct, buildRegistry, cacheBundle, checkCollision, loadCachedBundle, partialPolicy, resolveGs1, resolvePlain, withBestBefore, withQuantity, withSerial, holdsOf, palletCapacityOf, storageMismatch, type Registry, type ResolvedScan, findLocation } from "./registry.js";

const FLOW = RECEIVE_PALLET;
const DRAFT_KEY = "trail.receiveDraft";

type Phase =
  | { kind: "pick" }
  | { kind: "run"; carry: BannerData | null }
  | { kind: "pallet-done"; carry: BannerData }
  | { kind: "close" }
  | { kind: "closed"; alreadyClosed: boolean };

interface Draft {
  receiptId: string;
  locationId: string;
  run: RunState;
  records: ScanRecord[];
  issued: string[];
}

function loadDraft(): Draft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    return raw ? (JSON.parse(raw) as Draft) : null;
  } catch {
    return null;
  }
}
function saveDraft(d: Draft | null): void {
  try {
    if (d) localStorage.setItem(DRAFT_KEY, JSON.stringify(d));
    else localStorage.removeItem(DRAFT_KEY);
  } catch {
    /* quota */
  }
}

const shortSscc = (sscc: string) => `${sscc.slice(0, 4)}…${sscc.slice(-4)}`;
/** Goods as against a pallet: a carton with an identity, or a bulk quantity (owner 2026-11-03). */
const isGoods = (kind: string | undefined) => kind === "trade-item" || kind === "loose";

function useOnline(): boolean {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}

const isStuck = (q: QueuedStep) => Boolean(q.lastError && !/fetch|network|abort|5\d\d|401|429/i.test(q.lastError));

export function Receive({ onClose, mode = "purchase" }: { onClose: () => void; /** "return": customer returns against the ERP's return orders (owner 2026-11-03). */ mode?: "purchase" | "return" }) {
  const [phase, setPhase] = useState<Phase>({ kind: "pick" });
  const [registry, setRegistry] = useState<Registry | null>(() => {
    const cached = loadCachedBundle();
    return cached ? buildRegistry(cached) : null;
  });
  const [registryNote, setRegistryNote] = useState<string | null>(null);
  const [receipts, setReceipts] = useState<ExpectedReceiptSummary[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<ExpectedReceiptDetail | null>(null);
  const [locationId, setLocationId] = useState<string>(() => getPrefs().receivingLocationId ?? "");
  const [queue, setQueue] = useState<QueuedStep[]>(pending);
  const [draft, setDraft] = useState<Draft | null>(loadDraft);
  const [progress, setProgress] = useState<ReceiptProgress | null>(null);
  const [sessionPallets, setSessionPallets] = useState<{ sscc: string; n: number }[]>([]);
  const [palletSeq, setPalletSeq] = useState(0); // a new Run per pallet
  const online = useOnline();

  useEffect(() => {
    startQueueWorker();
    Api.bundle()
      .then((b) => {
        cacheBundle(b);
        setRegistry(buildRegistry(b));
        setRegistryNote(null);
      })
      .catch((e: unknown) => {
        const err = e instanceof Error ? e.message : String(e);
        setRegistryNote(registry ? t("pick.registryOld", { date: new Date(registry.bundle.generatedAt).toLocaleString("sv-SE"), err }) : t("pick.registryMissing", { err }));
      });
    Api.listReceipts(mode)
      .then(setReceipts)
      .catch((e: unknown) => setListError(e instanceof Error ? e.message : String(e)));
    return subscribe(setQueue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The receiving location is a site/device setting; a choice only where the site has several.
  useEffect(() => {
    if (!registry) return;
    const site = registry.bundle.siteConfig?.receiving.locationId;
    if (site && registry.receivingLocations.some((l) => l.id === site)) {
      if (locationId !== site) setLocationId(site);
      return;
    }
    const valid = registry.receivingLocations.some((l) => l.id === locationId);
    if (!valid) {
      const only = registry.receivingLocations.length === 1 ? registry.receivingLocations[0]!.id : "";
      setLocationId(only);
      if (only) savePrefs({ receivingLocationId: only });
    }
  }, [registry, locationId]);

  const refreshProgress = useCallback((id: string) => {
    Api.progress(id).then(setProgress).catch(() => undefined);
  }, []);

  const [syncing, setSyncing] = useState<string | null>(null);
  /** The receipt being opened: a spinner on its row (owner 2026-11-01: a text above the list is missed). */
  const [opening, setOpening] = useState<string | null>(null);
  const start = useCallback(
    async (id: string, resume: Draft | null = null) => {
      try {
        // Company setting: read the order from the ERP again before receiving starts (owner 2026-10-30).
        if (registry?.bundle.tenantConfig?.erp?.refreshOrderBeforeWork ?? true) {
          const ref = receipts?.find((r) => r.id === id)?.reference;
          if (ref) {
            setSyncing(t("erp.readingOrder", { ref }));
            const r = await refreshOrderNow("receipts", ref);
            setSyncing(null);
            if (r.mirrored && !r.fresh) setListError(t("erp.notReached.msg"));
          }
        }
        const detail = await Api.getReceipt(id);
        // Locked in the ERP (owner 2026-11-03: someone has the purchase order open in Visma): nothing starts.
        if (detail.erpLock) { feedback("rejected"); setListError(t("erp.lockedMsg", { ref: detail.reference, process: detail.erpLock.process, by: detail.erpLock.by ?? "" })); return; }
        setReceipt(detail);
        if (resume) setLocationId(resume.locationId);
        refreshProgress(id);
        setPhase({ kind: "run", carry: null });
      } catch (e) {
        setSyncing(null);
        setListError(e instanceof Error ? e.message : String(e));
      }
    },
    [refreshProgress, registry, receipts],
  );

  // An order number Trail has not seen: ask the ERP for it by number, then look again (owner 2026-10-30).
  const fetchFromErp = useCallback(async (ref: string) => {
    const r = await refreshOrderNow("receipts", ref);
    const list = await Api.listReceipts(mode);
    setReceipts(list);
    return { ...r, hit: list.find((x) => x.reference.toUpperCase() === ref.toUpperCase()) ?? null };
  }, []);
  // While receiving: when the mirror has run again, re-read the order and say so if it changed.
  useErpRuns(phase.kind === "run" && !!receipt, () => {
    if (!receipt) return;
    Api.getReceipt(receipt.id).then((detail) => {
      if (JSON.stringify(detail) === JSON.stringify(receipt)) return;
      setReceipt(detail);
      refreshProgress(receipt.id);
      setPhase({ kind: "run", carry: { kind: "warn", label: t("erp.orderChanged"), message: t("erp.orderChanged.msg") } });
    }).catch(() => undefined);
  });

  const onDraft = useCallback((d: Draft | null) => {
    saveDraft(d);
    setDraft(d);
  }, []);

  const backToPick = () => {
    setReceipt(null);
    setProgress(null);
    setPhase({ kind: "pick" });
    Api.listReceipts(mode).then(setReceipts).catch(() => undefined);
  };

  const net = { online, queued: queue.length, stuck: queue.filter(isStuck).length };

  if (phase.kind === "run" && registry && receipt && locationId) {
    return (
      <Run
        key={palletSeq}
        mode={mode}
        onLineAdded={() => Api.getReceipt(receipt.id).then(setReceipt).catch(() => undefined)}
        registry={registry}
        receipt={receipt}
        locationId={locationId}
        progress={progress}
        sessionPallets={sessionPallets}
        net={net}
        carry={phase.carry}
        resume={draft && draft.receiptId === receipt.id ? draft : null}
        onDraft={onDraft}
        onDone={(carry, sscc, n) => {
          setSessionPallets((p) => [{ sscc, n }, ...p].slice(0, 5));
          setPalletSeq((s) => s + 1);
          refreshProgress(receipt.id);
          setPhase((registry.bundle.siteConfig?.receiving.afterPallet ?? "next-pallet") === "next-pallet" ? { kind: "run", carry } : { kind: "pallet-done", carry });
        }}
        onCloseDelivery={() => setPhase({ kind: "close" })}
        onAbort={() => {
          onDraft(null);
          setPhase({ kind: "pick" });
        }}
      />
    );
  }

  if (phase.kind === "pallet-done" && receipt) {
    return (
      <div className="screen">
        <Strip left={<><b>{receipt.reference}</b> · {receipt.supplier?.name ?? receipt.customer?.name ?? ""}</>} middle={registry?.bundle.locations.find((l) => l.id === locationId)?.code} {...net} />
        <div className="body">
          <Banner {...phase.carry} />
          <Prompt quiet step={t("close.sub", { pallets: progress?.pallets ?? sessionPallets.length, containers: progress?.containers ?? 0 })} title={`${t("rows.ordered")} / ${t("rows.received")}`} />
          <ProgressRows receipt={receipt} progress={progress} />
        </div>
        <Actions>
          <Btn kind="quiet" icon="back" onClick={backToPick} />
          <Btn kind="secondary" onClick={() => setPhase({ kind: "close" })}>{t("done.closeDelivery")}</Btn>
          <Btn kind="primary" onClick={() => { setPalletSeq((s) => s + 1); setPhase({ kind: "run", carry: null }); }}>{t("done.next")}</Btn>
        </Actions>
      </div>
    );
  }

  if (phase.kind === "close" && receipt) {
    return <CloseDelivery receipt={receipt} net={net} queue={queue} siteConfig={registry?.bundle.siteConfig ?? null} needReasons={mode !== "return"} onClosed={(alreadyClosed) => setPhase({ kind: "closed", alreadyClosed })} onBack={() => setPhase({ kind: "run", carry: null })} />;
  }

  if (phase.kind === "closed" && receipt) {
    return (
      <div className="screen">
        <Strip left={<><b>{receipt.reference}</b> · {receipt.supplier?.name ?? receipt.customer?.name ?? ""}</>} {...net} />
        <div className="body">
          <Banner kind="ok" label={phase.alreadyClosed ? t("close.alreadyClosed") : t("close.closed")} message={`${receipt.reference} · ${receipt.supplier?.name ?? receipt.customer?.name ?? ""}`} />
        </div>
        <Actions>
          <Btn kind="primary" onClick={backToPick}>{t("done.receipts")}</Btn>
        </Actions>
      </div>
    );
  }

  return (
    <PickOrder
      mode={mode}
      registry={registry}
      registryNote={registryNote}
      receipts={receipts}
      listError={listError}
      syncing={syncing}
      onFetchFromErp={fetchFromErp}
      locationId={locationId}
      net={net}
      queue={queue}
      draft={draft}
      onChooseLocation={(id) => {
        setLocationId(id);
        savePrefs({ receivingLocationId: id });
      }}
      onStart={(id) => { setOpening(id); void Promise.resolve(start(id)).finally(() => setOpening(null)); }}
      opening={opening}
      onResume={() => draft && void start(draft.receiptId, draft)}
      onDiscardDraft={() => onDraft(null)}
      onClose={onClose}
    />
  );
}

// --- pick the purchase order ----------------------------------------------------

interface Net {
  online: boolean;
  queued: number;
  stuck: number;
}

interface PickProps {
  mode: "purchase" | "return";
  registry: Registry | null;
  registryNote: string | null;
  receipts: ExpectedReceiptSummary[] | null;
  listError: string | null;
  syncing: string | null;
  opening: string | null;
  onFetchFromErp: (ref: string) => Promise<{ mirrored: boolean; fresh: boolean; hit: ExpectedReceiptSummary | null }>;
  locationId: string;
  net: Net;
  queue: QueuedStep[];
  draft: Draft | null;
  onChooseLocation: (id: string) => void;
  onStart: (id: string) => void;
  onResume: () => void;
  onDiscardDraft: () => void;
  onClose: () => void;
}

function PickOrder({ mode, registry, registryNote, receipts, listError, syncing, opening, locationId, net, queue, draft, onChooseLocation, onStart, onResume, onDiscardDraft, onClose, onFetchFromErp }: PickProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [noMatch, setNoMatch] = useState<string | null>(null);
  const [fetching, setFetching] = useState<string | null>(null);
  const receivingLocations = registry?.receivingLocations ?? [];
  const location = receivingLocations.find((l) => l.id === locationId);
  const stuckItem = queue.find(isStuck);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    focusScan(el);
    return attachWedge(el, {
      onScan: (d) => {
        const ref = d.replace(/^\]\w\w/, "").trim().toUpperCase();
        const hit = receipts?.find((r) => r.reference.toUpperCase() === ref);
        if (hit && locationId) {
          feedback("accepted");
          onStart(hit.id);
          return;
        }
        // An order number Trail does not have: the ERP may (owner 2026-10-30: operators are used to
        // waiting a few seconds for an order). Digits only — anything else is not an order number.
        if (/^\d{3,12}$/.test(ref) && locationId) {
          setNoMatch(null);
          setFetching(ref);
          void onFetchFromErp(ref).then((r) => {
            setFetching(null);
            if (r.hit) { feedback("accepted"); onStart(r.hit.id); return; }
            feedback("rejected");
            setNoMatch(r.mirrored ? t("erp.orderNotInErp", { ref }) : t("pick.noMatch", { ref }));
          }).catch(() => { setFetching(null); feedback("rejected"); setNoMatch(t("pick.noMatch", { ref })); });
          return;
        }
        feedback("rejected");
        // Not an order number: say what it was instead (owner 2026-10-29: a product label
        // scanned here got "no open order with number ]C001…").
        void (async () => {
          const aim = /^\]\w\w/.exec(d)?.[0] ?? null;
          let e: GS1Elements | null = null;
          if (!(aim && !isGs1Symbology(d))) {
            try { e = await parseScan(d); } catch { e = null; }
          }
          const loc = registry ? findLocation(registry, ref) : null;
          if (e?.sscc) setNoMatch(t("pick.notOrder.pallet"));
          else if (e?.gtin) setNoMatch(t("pick.notOrder.item", { what: registry?.byGtin.get(e.gtin)?.product.name ?? `GTIN ${e.gtin}` }));
          else if (loc) setNoMatch(t("pick.notOrder.location", { code: loc.code }));
          else setNoMatch(t("pick.noMatch", { ref: ref.slice(0, 40) }));
        })();
      },
    });
  }, [receipts, locationId, onStart, registry, onFetchFromErp]);

  return (
    <div className="screen" onClick={() => { unlockAudio(); focusScan(inputRef.current); }}>
      <Strip left={<b>{t("menu.receiving")} · {t("menu.pallet")}</b>} middle={location ? `${t("pick.location")}: ${location.code}` : undefined} {...net} />
      <div className="body">
        {registryNote && <Banner kind="warn" label={t("banner.rejected")} message={registryNote} />}
        {stuckItem && (
          <>
            <Banner kind="bad" label={t("strip.queueStuck", { n: net.stuck })} message={stuckItem.lastError ?? ""} />
            <Btn kind="danger" onClick={() => { if (window.confirm(t("queue.discardConfirm"))) discard(stuckItem.id); }}>{t("queue.discard")}</Btn>
          </>
        )}
        {draft && (
          <>
            <Banner kind="warn" label={t("resume.title", { n: draft.records.length })} message="" />
            <div className="chips">
              <button type="button" className="chip on" onClick={(e) => { e.stopPropagation(); onResume(); }}>{t("resume.resume")}</button>
              <button type="button" className="chip" onClick={(e) => { e.stopPropagation(); onDiscardDraft(); }}>{t("resume.discard")}</button>
            </div>
          </>
        )}
        <Prompt step={t("pick.step")} title={mode === "return" ? t("ret.title") : t("pick.title")} sub={mode === "return" ? t("ret.sub") : t("pick.sub")} />
        <ScanBar inputRef={inputRef} placeholder={t("pick.scan")} />
        {noMatch && <Banner kind="bad" label={t("banner.rejected")} message={noMatch} />}
        {receivingLocations.length === 0 && registry && <Banner kind="bad" label={t("pick.location")} message={t("pick.noLocation")} />}
        {receivingLocations.length > 1 && (
          <div className="field">
            <span>{t("pick.chooseLocation")}</span>
            <Chips options={receivingLocations.map((l) => ({ value: l.id, label: l.code }))} value={locationId || null} onChange={onChooseLocation} />
          </div>
        )}
        {listError && <Banner kind="bad" label={t("banner.rejected")} message={listError} />}
        {receipts === null && !listError && <p className="hint">{t("common.loading")}</p>}
        {receipts && receipts.length === 0 && <p className="hint">{t("pick.none")}</p>}
        <div className="list">
          {/* The order being fetched from the ERP stands first, as a row of its own (owner 2026-11-02: the
              scanner's beep came at once but nothing showed for seconds). */}
          {fetching && <ListItem title={fetching} desc={<b>{t("erp.fetchingRow")}</b>} right={<span className="spin" aria-label={t("common.loading")} />} />}
          {receipts?.map((r) => (
            <ListItem
              key={r.id}
              title={r.reference}
              tag={r.erpLock ? t("erp.lockedTag") : undefined}
              tagKind={r.erpLock ? "bad" : undefined}
              desc={opening === r.id ? <b>{syncing ?? t("common.loading")}</b> : [r.erpLock ? t("erp.lockedTag") : null, (r.supplier ?? r.customer)?.name, r.kind === "return" && r.erpWarehouse ? t("ret.warehouse", { n: r.erpWarehouse }) : null, r.lines === 1 ? t("pick.line") : t("pick.lines", { n: r.lines }), r.expectedAt ? t("pick.expected", { date: r.expectedAt }) : null, r.unresolvedLines > 0 ? t("pick.unmapped", { n: r.unresolvedLines }) : null, r.status === "proposed" ? t("pick.proposed") : null].filter(Boolean).join(" · ")}
              onClick={registry && locationId && !opening ? () => onStart(r.id) : undefined}
              right={opening === r.id ? <span className="spin" aria-label={t("common.loading")} /> : undefined}
            />
          ))}
        </div>
      </div>
      <Actions>
        <Btn kind="quiet" icon="back" onClick={onClose}>{t("common.menu")}</Btn>
      </Actions>
    </div>
  );
}

// --- the flow run ---------------------------------------------------------------

interface RunProps {
  /** "return": goods the order has no line for get a line created at the door, with the ERP warehouse (owner 2026-11-03). */
  mode: "purchase" | "return";
  onLineAdded: () => void;
  registry: Registry;
  receipt: ExpectedReceiptDetail;
  locationId: string;
  progress: ReceiptProgress | null;
  sessionPallets: { sscc: string; n: number }[];
  net: Net;
  carry: BannerData | null;
  resume: Draft | null;
  onDraft: (d: Draft | null) => void;
  onDone: (carry: BannerData, sscc: string, n: number) => void;
  onCloseDelivery: () => void;
  onAbort: () => void;
}

function stepPrompt(state: RunState): string {
  const s = state.stepId ? FLOW.steps.find((x) => x.id === state.stepId) : null;
  return s?.id === "pallet" ? t("pallet.title") : s?.id === "containers" ? t("many.title") : s?.title || "";
}

function unitWord(scan: ResolvedScan | null): string {
  const info = scan?.gtinInfo ?? null;
  if (info?.displayName) return info.displayName;
  switch (info?.level) {
    case "case":
      return t("unit.case");
    case "inner":
      return t("unit.inner");
    case "each":
      return t("unit.each");
    case "pallet":
      return t("unit.pallet");
    default:
      return t("unit.kolli");
  }
}

function quantityTitle(uom: string): string {
  switch (uom) {
    case "MTR":
      return "Length (m)";
    case "KGM":
      return "Net weight (kg)";
    case "EA":
      return "Count";
    default:
      return `Quantity (${uomLabel(uom)})`;
  }
}

/** The signature: the scanned unit shown like the middle block of its GS1 label. */
function labelCells(scan: ResolvedScan): LabelCell[] {
  const e = scan.input.parsed;
  const cells: LabelCell[] = [];
  if (e.sscc) {
    cells.push({ title: "SSCC", value: e.sscc, wide: true });
    return cells;
  }
  if (e.gtin) cells.push({ title: "GTIN", value: e.gtin, wide: true });
  cells.push({ title: "Batch/Lot", value: e.lot ?? <small>{t("label.missing")}</small> });
  if (e.expiry) cells.push({ title: "Use by", value: e.expiry });
  else cells.push({ title: "Best before", value: e.bestBefore ? <>{e.bestBefore}{scan.entered?.bestBefore ? <small> {t("label.manual")}</small> : null}</> : <small>{t("label.missing")}</small> });
  if (e.serial) cells.push({ title: "Serial", value: e.serial, wide: true });
  if (scan.quantity) {
    cells.push({ title: quantityTitle(scan.quantity.uom), value: <>{fmtNum(scan.quantity.value)} <small>{uomLabel(scan.quantity.uom)}</small></>, wide: true });
  }
  return cells;
}

/** Digits typed become YYYY-MM-DD as they come; a full ISO date passes through. */
function formatIsoDate(v: string): string {
  const d = v.replace(/\D/g, "").slice(0, 8);
  if (d.length <= 4) return d;
  if (d.length <= 6) return `${d.slice(0, 4)}-${d.slice(4)}`;
  return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6)}`;
}

/** A real calendar date, not in the past (a best-before read at receiving). */
function isoDateOk(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const ms = Date.parse(`${s}T00:00:00Z`);
  return !Number.isNaN(ms) && new Date(ms).toISOString().slice(0, 10) === s && s >= new Date().toISOString().slice(0, 10);
}

function describeScan(s: ResolvedScan): string {
  const e = s.input.parsed;
  const r = s.input.resolved;
  if (r.as === "location") return `${t("pick.location")} ${s.location?.code ?? r.locationId}`;
  if (e.sscc) return `${t("unit.pallet")} ${e.sscc}`;
  if (e.gtin) return `${s.product?.name ?? `GTIN ${e.gtin}`}${e.lot ? ` · ${e.lot}` : ""}`;
  return `"${s.raw.slice(0, 40)}"`;
}

function deviationText(d: ResolvedScan["deviations"][number]): string {
  return d === "unknown-product" ? t("dev.unknownProduct") : d === "missing-lot" ? t("dev.missingLot") : d === "missing-best-before" ? t("dev.missingBestBefore") : t("dev.quantityUnknown");
}

function Run({ mode, onLineAdded, registry, receipt, locationId, progress, sessionPallets, net, carry, resume, onDraft, onDone, onCloseDelivery, onAbort }: RunProps) {
  const settings = useMemo(getSettings, []);
  const inputRef = useRef<HTMLInputElement>(null);
  const recordsRef = useRef<Map<string, ScanRecord>>(new Map(resume?.records.map((r) => [r.containerId, r]) ?? []));
  const issuedRef = useRef<Set<string>>(new Set(resume?.issued ?? []));
  /** An SSCC label went to a printer at the start: the pallet then exists physically and is worth resuming. */
  const printedRef = useRef<boolean>((resume?.issued?.length ?? 0) > 0);
  const assembler = useMemo(() => createAssembler(), []);
  const onDraftRef = useRef(onDraft);
  onDraftRef.current = onDraft;
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const [run, setRun] = useState<RunState>(() => resume?.run ?? startRun(FLOW, { expected: { count: null }, dock: locationId, receipt: asValue({ id: receipt.id, reference: receipt.reference }) }).state);
  const runRef = useRef(run);
  runRef.current = run;
  const [version, setVersion] = useState(0);
  const [banner, setBanner] = useState<BannerData | null>(carry);
  useEffect(() => { if (carry) setBanner(carry); }, [carry]);
  const [lastScan, setLastScan] = useState<ResolvedScan | null>(() => {
    const recs = [...recordsRef.current.values()];
    return recs.length > 0 ? recs[recs.length - 1]!.scan : null;
  });
  const [held, setHeld] = useState(0);
  const [camera, setCamera] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [posting, setPosting] = useState(false);
  const [sheet, setSheet] = useState<"more" | "status" | "abandon" | "back" | "manual" | null>(null);
  /** Receiving without a scan (owner 2026-11-01): the label's facts typed, a required comment; the carton gets its identity and a label. */
  const [manual, setManual] = useState<{ gtin: string; name: string; uom: string; lot: string; bestBefore: string; qty: string; note: string; /** A bulk product (owner 2026-11-03): quantity and comment only, no batch. */ bulk?: boolean } | null>(null);
  const [tallyReason, setTallyReason] = useState<ReasonCode | null>(null);
  const tallyConfirmRef = useRef<{ expected: number; scanned: number; reason: string } | null>(null);
  /** A scan held for a value the operator must give first (mockup follow-ups A and B). */
  const [pending, setPending] = useState<{ kind: "serial" | "best-before" | "quantity" | "warehouse"; scan: ResolvedScan; at: string | null } | null>(null);
  /** The ERP warehouses a receiver may choose for a return line (owner 2026-11-03): the head's, and every warehouse a zone points at. */
  const warehouseChoices = useMemo(() => {
    const set = new Set<string>();
    if (receipt.erpWarehouse) set.add(receipt.erpWarehouse);
    for (const l of registry.bundle.locations) if (l.erpWarehouse) set.add(l.erpWarehouse);
    return [...set].sort((a, b) => a.localeCompare(b, "sv", { numeric: true }));
  }, [receipt.erpWarehouse, registry]);
  const [warehouseChoice, setWarehouseChoice] = useState<string | null>(null);
  const [entry, setEntry] = useState("");
  const [entrySource, setEntrySource] = useState<string | null>(null);
  const entryRef = useRef<HTMLInputElement>(null);
  const cfg = registry.bundle.siteConfig?.receiving;

  const location = registry.bundle.locations.find((l) => l.id === locationId);
  const step = run.stepId ? FLOW.steps.find((s) => s.id === run.stepId) : null;
  const palletData = run.data["pallet"] as { containerId: string; identityKey: string | null } | null | undefined;
  const palletSscc = palletData?.identityKey?.replace("sscc:00=", "") ?? null;
  const count = run.many["containers"]?.items.length ?? 0;

  useEffect(() => {
    if (run.status === "completed") return;
    // A pallet is worth resuming once something physical exists: scans, or an SSCC label
    // printed at the start. A pallet merely begun is not (owner 2026-10-28).
    const printedAtStart = issuedRef.current.size > 0 && printedRef.current;
    if (recordsRef.current.size === 0 && !printedAtStart) {
      onDraftRef.current(null);
      return;
    }
    onDraftRef.current({ receiptId: receipt.id, locationId, run, records: [...recordsRef.current.values()], issued: [...issuedRef.current] });
  }, [run, version, receipt.id, locationId, registry]);

  const complete = useCallback(
    async (state: RunState) => {
      setPosting(true);
      const batch = buildStepBatch(state, recordsRef.current, {
        settings,
        goodsOwnerId: registry.bundle.goodsOwner.id,
        locationId,
        receipt: { id: receipt.id, supplierPartyId: receipt.supplier?.id ?? null },
        flow: { id: FLOW.id, version: FLOW.version },
        stepId: "containers",
        tallyConfirmation: tallyConfirmRef.current,
        directToStorage: registry.bundle.siteConfig?.receiving?.directToStorage ?? true,
      });
      const dockId = state.data["dock"];
      const directTo = typeof dockId === "string" && dockId !== locationId ? registry.bundle.locations.find((l) => l.id === dockId)?.code ?? null : null;
      const n = [...recordsRef.current.values()].filter((r) => r.scan.input.resolved.as === "container" && isGoods(r.scan.input.resolved.containerKind)).length;
      const sscc = (state.data["pallet"] as { identityKey: string | null } | null)?.identityKey?.replace("sscc:00=", "") ?? "";
      const palletId = (state.data["pallet"] as { containerId: string } | null)?.containerId;
      let result: "stored" | "queued" = "queued";
      try {
        result = await submitStep(batch, palletId ? [{ type: "pallet.closed", flow: "receiving", containerId: palletId }] : undefined);
      } catch {
        result = "queued";
      }
      onDraftRef.current(null);
      feedback("accepted");
      let printNote = "";
      if (result === "stored" && palletId) {
        // "Pallen klar" is a moment; what prints is the automations' business (spec §6.8). The
        // content label needs the pallet stored first. No printer is not an error for the operator.
        try {
          const { prints } = await Api.automationEvent({ type: "pallet.closed", flow: "receiving", containerId: palletId });
          if (prints > 0) printNote = ` · ${t("msg.labelQueued")}`;
        } catch {
          printNote = ` · ${t("msg.labelNotPrinted")}`;
        }
      }
      const direct = directTo ? ` · ${t("msg.directStored", { code: directTo })}` : "";
      const next: BannerData = result === "stored" ? { kind: "ok", label: t("banner.saved"), message: t("msg.saved", { n, sscc }) + direct + printNote } : { kind: "warn", label: t("banner.savedQueued"), message: t("msg.savedQueued", { n }) + direct };
      onDoneRef.current(next, sscc, n);
    },
    [settings, registry, locationId, receipt],
  );

  const handleEffects = useCallback(
    (effects: Effect[], state: RunState) => {
      for (const fx of effects) if (fx.kind === "completed") void complete(state);
    },
    [complete],
  );

  const feed = useCallback(
    (scan: ResolvedScan, opts?: { skipEntry?: boolean; capacityChecked?: boolean }): CodeState => {
      const before = runRef.current;
      const r = scan.input.resolved;
      // A storage location as the pallet's last scan (owner 2026-11-02): received and put away in one go,
      // when the site allows it and the place takes the pallet — what it holds, how many pallets, its class.
      if (before.stepId === "containers" && r.as === "location" && r.locationId !== locationId && (before.many["containers"]?.items.length ?? 0) > 0) {
        const loc = scan.location ?? registry.bundle.locations.find((l) => l.id === r.locationId) ?? null;
        if (!(registry.bundle.siteConfig?.receiving?.directToStorage ?? true) || !loc || !loc.roles.includes("storage")) {
          feedback("rejected");
          setBanner({ kind: "bad", label: t("banner.rejected"), message: t("recv.notDock", { code: loc?.code ?? "" }) });
          return "rejected";
        }
        const hasPallet = !!(before.data["pallet"] as { containerId?: string } | null)?.containerId;
        const holds = holdsOf(registry, loc.id);
        if ((hasPallet && holds === "loose") || (!hasPallet && holds === "pallets")) {
          feedback("rejected");
          setBanner({ kind: "bad", label: t("banner.rejected"), message: hasPallet ? t("move.noPalletsHere", { code: loc.code }) : t("putaway.needsPallet", { code: loc.code }) });
          return "rejected";
        }
        const products = [...recordsRef.current.values()].map((x) => x.scan.product?.id ?? null).filter((x): x is string => !!x);
        const mm = products.map((pid) => storageMismatch(registry, pid, loc.id)).find((m) => m !== null) ?? null;
        if (mm?.action === "refuse") {
          feedback("rejected");
          setBanner({ kind: "bad", label: t("storage.wrong"), message: t("storage.wrongMsg", { need: mm.need, has: mm.has, code: loc.code }) });
          return "rejected";
        }
        const capacity = palletCapacityOf(registry, loc.id);
        if (hasPallet && capacity > 0 && !opts?.capacityChecked) {
          // Room for the pallet: asked of the server, then the scan is fed again with the answer.
          void Api.locationContents(loc.id).then((cs) => {
            const n = cs.filter((c) => c.kind === "logistic-unit").length;
            if (n >= capacity) { feedback("rejected"); setBanner({ kind: "bad", label: t("banner.rejected"), message: t("move.full", { code: loc.code, n, cap: capacity }) }); return; }
            feed(scan, { ...opts, capacityChecked: true });
          }).catch(() => feed(scan, { ...opts, capacityChecked: true }));
          return "partial";
        }
        if (mm) setBanner({ kind: "warn", label: t("storage.wrong"), message: t("storage.wrongMsg", { need: mm.need, has: mm.has, code: loc.code }) });
      }
      if (before.stepId === "containers" && (r.as === "location" || r.as === "done-code") && (before.many["containers"]?.items.length ?? 0) === 0) {
        feedback("rejected");
        setBanner({ kind: "bad", label: t("banner.rejected"), message: t("msg.emptyPallet") });
        return "rejected";
      }
      // An article that is not on the purchase order (owner 2026-11-02): refused by default; a site may
      // take it with a deviation, or quietly. Nothing to check on a blind receipt without lines.
      // A return (owner 2026-11-03): goods the order has no line for get a line created here — the receiver picks the
      // ERP warehouse when the company says so (the head's proposed), else the head's warehouse goes straight on.
      if (!opts?.skipEntry && mode === "return" && before.stepId === "containers" && r.as === "container" && isGoods(r.containerKind) && scan.product) {
        const pid = scan.product.id;
        const onOrder = receipt.lineItems.some((l) => l.productId === pid);
        if (!onOrder) {
          if (registry.bundle.tenantConfig?.erp?.returns?.warehousePerLine ?? true) {
            feedback("attention");
            setPending({ kind: "warehouse", scan, at: null });
            setWarehouseChoice(receipt.erpWarehouse ?? warehouseChoices[0] ?? null);
            setBanner({ kind: "warn", label: t("entry.warehouseTitle"), message: t("msg.lineNeeded", { what: scan.product.name }) });
            return "partial";
          }
          // The head's warehouse goes straight on: the line is created and the scan fed again once it is there.
          const held = scan;
          void Api.addReceiptLine(receipt.id, { productId: pid, erpWarehouse: receipt.erpWarehouse ?? null })
            .then(() => { onLineAdded(); feed(held, { skipEntry: true }); })
            .catch((e: unknown) => { feedback("rejected"); setBanner({ kind: "bad", label: t("banner.rejected"), message: e instanceof Error ? e.message : String(e) }); });
          return "partial";
        }
      }
      if (mode !== "return" && before.stepId === "containers" && r.as === "container" && isGoods(r.containerKind) && receipt.lineItems.length > 0) {
        const pid = scan.product?.id ?? null;
        const gtin = scan.input.parsed.gtin ?? null;
        const lineGtinCounts = (registry.bundle.tenantConfig?.erp?.lineGtin ?? "register") === "line";
        const onOrder = receipt.lineItems.some((l) => (pid !== null && l.productId === pid) || (lineGtinCounts && gtin !== null && l.gtin === gtin));
        const rule = cfg?.outsideOrder ?? "refuse";
        if (!onOrder && rule === "refuse") {
          feedback("rejected");
          setBanner({ kind: "bad", label: t("banner.rejected"), message: t("msg.notOnOrder", { what: scan.product?.name ?? `GTIN ${gtin ?? ""}`, ref: receipt.reference }) });
          return "rejected";
        }
      }
      // A required best-before missing on the label: ask for it before the container counts
      // (site setting missingRequired decides whether "receive without" is offered).
      if (!opts?.skipEntry && before.stepId === "containers" && r.as === "container" && r.containerKind === "trade-item" && scan.deviations.includes("missing-best-before") && !scan.entered?.bestBefore) {
        feedback("attention");
        setPending({ kind: "best-before", scan, at: null });
        setEntry("");
        setEntrySource(null);
        setBanner({ kind: "warn", label: t("banner.bestBeforeMissing"), message: t("msg.bestBeforeMissing", { profile: scan.rules?.profileName ?? "" }) });
        return "partial";
      }
      // A bulk product (owner 2026-11-03: no identity, quantity only): the operator types the quantity —
      // nothing is proposed, not even the register's standard; the row is received with it.
      if (!opts?.skipEntry && before.stepId === "containers" && r.as === "container" && r.containerKind === "loose" && scan.bulk && scan.quantity === null) {
        feedback("attention");
        setPending({ kind: "quantity", scan, at: null });
        setEntry("");
        setEntrySource("bulk");
        setBanner({ kind: "warn", label: t("banner.bulkQty"), message: t("msg.bulkQty", { what: scan.product?.name ?? "" }) });
        return "partial";
      }
      // No quantity in the barcode (owner 2026-11-03): never auto-confirmed. The register's standard is
      // proposed for a batch-handled article (prefilled, source shown); otherwise the field is empty.
      if (!opts?.skipEntry && before.stepId === "containers" && r.as === "container" && r.containerKind === "trade-item" && scan.deviations.includes("quantity-missing") && scan.entered?.quantity === undefined) {
        feedback("attention");
        const std = scan.rules?.capture.lot !== "forbidden" ? scan.product?.standardQuantity ?? null : null;
        setPending({ kind: "quantity", scan, at: null });
        setEntry(std ?? "");
        setEntrySource(std !== null ? "standard" : null);
        setBanner({ kind: "warn", label: t("banner.quantityMissing"), message: std !== null ? t("msg.quantityMissingStd", { q: std, uom: unitWord(scan) }) : t("msg.quantityMissing") });
        return "partial";
      }
      const { state, effects } = applyInput(FLOW, before, scan.input);
      const fb = effects.find((e): e is Extract<Effect, { kind: "feedback" }> => e.kind === "feedback");
      const accepted = fb?.result === "accepted";
      if (accepted && r.as === "container") {
        recordsRef.current.set(r.containerId, { containerId: r.containerId, scan, at: new Date().toISOString() });
        setLastScan(scan);
        setVersion((v) => v + 1);
      }
      if (accepted && r.as === "container" && r.containerKind === "logistic-unit") {
        feedback(scan.issuedHere ? "attention" : "accepted");
        // A pallet Trail just numbered needs no banner: its number stands at the top of the screen.
        setBanner(scan.issuedHere ? null : { kind: "ok", label: t("banner.accepted"), message: describeScan(scan) });
      } else if (accepted && scan.deviations.length > 0) {
        feedback("attention");
        setBanner({ kind: "warn", label: t("banner.acceptedDeviation"), message: `${scan.deviations.map(deviationText).join(", ")}. ${t("dev.counted")}` });
      } else if (accepted) {
        feedback("accepted");
        const q = scan.quantity ? `${fmtQty(scan.quantity.value, scan.quantity.uom)} · ` : "";
        setBanner({ kind: "ok", label: t("banner.accepted"), message: `${q}${scan.product?.name ?? `GTIN ${scan.input.parsed.gtin ?? ""}`}` });
      } else if (fb) {
        feedback(fb.result);
        if (fb.result === "rejected") {
          const dup = /already scanned/.test(fb.message);
          const wrong = r.as === "location" ? t("msg.wrongKind.location", { code: scan.location?.code ?? "" }) : r.as === "container" && r.containerKind === "logistic-unit" && before.stepId === "containers" ? t("msg.wrongKind.pallet") : r.as === "container" && before.stepId === "pallet" ? t("msg.wrongKind.item", { what: describeScan(scan) }) : r.as === "unknown" ? t("msg.unknownCode", { raw: scan.raw.slice(0, 40) }) : fb.message;
          setBanner({ kind: "bad", label: dup ? t("banner.rejectedDuplicate") : t("banner.rejected"), message: dup ? t("msg.duplicate", { what: describeScan(scan) }) : wrong });
        } else {
          setBanner({ kind: "warn", label: t("banner.tally"), message: fb.message });
        }
      }
      runRef.current = state;
      setRun(state);
      handleEffects(effects, state);
      return accepted ? "accepted" : fb?.result === "attention" ? "partial" : "rejected";
    },
    [handleEffects],
  );

  /** A container identity already in stock scanned again (§4.5): refuse it, count it (any
   *  day or the same day only), or ask for a serial that tells the two apart. */
  const guarded = useCallback(
    async (scan: ResolvedScan): Promise<CodeState> => {
      const r = scan.input.resolved;
      const policy = cfg?.collision ?? "block";
      // A kolli scanned on the pallet step is the wrong kind for the step, whatever stock says:
      // the step's own answer ("scan the pallet first") comes before any collision check
      // (owner 2026-10-29: a received carton scanned in step 2 got "already in stock").
      const wrongStep = runRef.current.stepId === "pallet" && r.as === "container" && isGoods(r.containerKind);
      if (!wrongStep && r.as === "container" && r.identityKey && !scan.issuedHere && (policy !== "count" || cfg?.collisionSameDayOnly)) {
        const lookup = (key: string, ms: number) => Api.containersByIdentity(key, ms);
        const hit = await checkCollision(registry, r.identityKey, lookup);
        if (hit) {
          const at = hit.locationCode ? t("msg.collisionAt", { code: hit.locationCode }) : "";
          const today = new Date().toISOString().slice(0, 10);
          if (policy === "count") {
            if (hit.receivedAt?.slice(0, 10) === today) return feed(scan);
            feedback("rejected");
            setBanner({ kind: "bad", label: t("banner.collision"), message: t("msg.collisionOtherDay", { what: describeScan(scan), date: hit.receivedAt?.slice(0, 10) ?? "?", at }) });
            return "rejected";
          }
          if (policy === "ask-serial" && r.containerKind === "trade-item" && scan.rules?.capture.serial !== "forbidden") {
            if (scan.input.parsed.serial) {
              // The label carries AI 21: the serial keys the container; only a second identical serial collides.
              const keyed = withSerial(scan, scan.input.parsed.serial);
              const again = keyed.input.resolved.as === "container" && keyed.input.resolved.identityKey ? await checkCollision(registry, keyed.input.resolved.identityKey, lookup) : null;
              if (!again) return feed(keyed);
            } else {
              feedback("attention");
              setPending({ kind: "serial", scan, at: hit.locationCode });
              setEntry("");
              setBanner({ kind: "warn", label: t("banner.collisionHold"), message: t("msg.collisionHold", { what: describeScan(scan), at }) });
              return "partial";
            }
          }
          if (policy === "ask-serial" && cfg?.collisionEscape === "count") return feed(scan);
          feedback("rejected");
          setBanner({ kind: "bad", label: t("banner.collision"), message: t("msg.collision", { what: describeScan(scan), at }) });
          return "rejected";
        }
      }
      return feed(scan);
    },
    [registry, feed, cfg],
  );

  /** One pipeline for wedge, camera and typed input (D7-2). */
  const handleRaw = useCallback(
    async (data: string, mark?: (d: string, s: CodeState) => void, source: "wedge" | "camera" | "manual" = "wedge", note?: string, preset?: { quantity: number }): Promise<CodeState> => {
      const aim = /^\]\w\w/.exec(data)?.[0] ?? null;
      if (aim && !isGs1Symbology(data)) return feed(resolvePlain(registry, data.slice(3), aim, data));
      let elements: GS1Elements;
      try {
        elements = await parseScan(data);
      } catch (err) {
        if (!aim && !/[()\u001d]/.test(data) && data.length <= 40) return feed(resolvePlain(registry, data, null, data));
        feedback("rejected");
        setBanner({ kind: "bad", label: t("banner.rejected"), message: t("msg.parseError", { err: err instanceof GS1ParseError ? err.message : String(err) }) });
        return "rejected";
      }
      const policy = elements.sscc !== null || elements.ai["254"] !== undefined ? { complete: true, action: "accept" as const } : partialPolicy(registry, elements);
      const merged = await assembler.tryAssemble(elements);
      if (merged) {
        for (const d of assembler.takeHeld()) mark?.(d, "accepted");
        setHeld(0);
        return guarded({ ...resolveGs1(registry, merged, data, { assembled: true, issuedSsccs: issuedRef.current }), source, ...(note ? { note } : {}) });
      }
      if (!policy.complete && policy.action === "reject") {
        feedback("rejected");
        setBanner({ kind: "bad", label: t("banner.rejected"), message: t("msg.partialRejected") });
        return "rejected";
      }
      if (!policy.complete && policy.action === "hold" && elements.gtin) {
        assembler.hold(data, elements);
        setHeld(assembler.heldCount());
        feedback("attention");
        setBanner({ kind: "warn", label: t("banner.waitingLot"), message: t("msg.waitingLot", { gtin: elements.gtin }) });
        return "partial";
      }
      const resolved = resolveGs1(registry, elements, data, { assembled: false, issuedSsccs: issuedRef.current });
      // A bulk product received by hand (owner 2026-11-03): the quantity typed on the sheet is the quantity, no second entry.
      const scan = preset && resolved.bulk ? withQuantity(resolved, String(preset.quantity), null) : resolved;
      return guarded({ ...scan, source, ...(note ? { note } : {}) });
    },
    [registry, assembler, feed, guarded],
  );

  const acceptHeldWithoutLot = async () => {
    const [first] = assembler.takeHeld();
    setHeld(0);
    setSheet(null);
    if (!first) return;
    try {
      const elements = await parseScan(first);
      feed(resolveGs1(registry, elements, first, { assembled: false, issuedSsccs: issuedRef.current }));
    } catch {
      /* already reported when held */
    }
  };

  // The camera closes itself after an accepted scan on a single-scan step (pallet, location);
  // on the kolli step it stays up for the next one (owner 2026-10-28: could not get out).
  const onCameraCode = useCallback(async (data: string, _format: string, mark: (d: string, s: CodeState) => void) => {
    const st = await handleRaw(data, mark, "camera");
    const cur = runRef.current.stepId ? FLOW.steps.find((s) => s.id === runRef.current.stepId) : null;
    if (st === "accepted" && cur?.type !== "scan_many") setCamera(false);
    return st;
  }, [handleRaw]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el || camera || sheet || pending) return;
    focusScan(el);
    return attachWedge(el, { onScan: (d) => void handleRaw(d) });
  }, [handleRaw, camera, sheet, pending]);
  // The step changed (pallet → containers): the field is the thing to look at again, even
  // though the button that ended the previous step took the focus.
  useEffect(() => {
    if (!camera && !sheet && !pending) focusScan(inputRef.current);
  }, [run.stepId, camera, sheet, pending]);

  /** The typed facts as the GS1 element string the label would have carried, fed through the same pipeline as a scan. */
  const manualReceive = async () => {
    if (!manual) return;
    const qty = Number(manual.qty.replace(",", "."));
    if (!Number.isFinite(qty) || qty <= 0) { setBanner({ kind: "warn", label: t("recv.manual"), message: t("recv.manualBadQty") }); return; }
    const u = manual.uom.toUpperCase();
    const fixed = (ai: string, n: number) => `(${ai})${String(Math.round(n * 1000)).padStart(6, "0")}`;
    const qtyAi = u === "MTR" || u === "M" ? fixed("3113", qty) : u === "KGM" || u === "KG" ? fixed("3103", qty) : `(30)${Math.round(qty)}`;
    const bb = manual.bestBefore.trim() ? `(15)${manual.bestBefore.replace(/-/g, "").slice(2, 8)}` : "";
    // A bulk product: the barcode names the article, the typed quantity goes straight in (no batch, no entry step).
    const str = manual.bulk ? `(01)${manual.gtin}${qtyAi}` : `(01)${manual.gtin}(10)${manual.lot.trim()}${bb}${qtyAi}`;
    setSheet(null);
    const st = await handleRaw(str, undefined, "manual", manual.note.trim(), manual.bulk ? { quantity: qty } : undefined);
    if (st === "accepted") setBanner({ kind: "warn", label: t("recv.manual"), message: manual.bulk ? t("recv.manualDoneBulk", { q: fmtQty(qty, manual.uom) }) : t("recv.manualDone", { lot: manual.lot.trim() }) });
    setManual(null);
  };
  const newPallet = async () => {
    setIssuing(true);
    try {
      const { sscc } = await Api.issueSscc();
      issuedRef.current.add(sscc);
      await handleRaw(`(00)${sscc}`);
      Api.automationEvent({ type: "pallet.started", flow: "receiving", sscc }).then((r) => { if (r.prints > 0) printedRef.current = true; }).catch(() => undefined);
    } catch (e) {
      feedback("rejected");
      setBanner({ kind: "bad", label: t("banner.rejected"), message: e instanceof Error ? e.message : String(e) });
    } finally {
      setIssuing(false);
    }
  };

  const endPallet = () => {
    const { state, effects } = applyInput(FLOW, runRef.current, { kind: "terminate" });
    runRef.current = state;
    setRun(state);
    const fb = effects.find((e): e is Extract<Effect, { kind: "feedback" }> => e.kind === "feedback");
    if (fb && fb.result !== "accepted") {
      feedback(fb.result);
      setBanner({ kind: fb.result === "attention" ? "warn" : "bad", label: fb.result === "attention" ? t("banner.tally") : t("banner.rejected"), message: fb.message });
    }
    handleEffects(effects, state);
  };

  const confirmTally = () => {
    const expected = Number(/of (\d+)/.exec(banner?.message ?? "")?.[1] ?? NaN);
    tallyConfirmRef.current = { expected, scanned: count, reason: tallyReason ?? "other" };
    const { state, effects } = applyInput(FLOW, runRef.current, { kind: "confirm" });
    runRef.current = state;
    setRun(state);
    handleEffects(effects, state);
  };

  /** Back from the container step to the pallet step: the pallet's scans are dropped, the
   *  issued SSCC stays known so its label can be scanned again. */
  const restartAtPallet = () => {
    recordsRef.current.clear();
    const s = startRun(FLOW, { expected: { count: null }, dock: locationId, receipt: asValue({ id: receipt.id, reference: receipt.reference }) }).state;
    runRef.current = s;
    setRun(s);
    setLastScan(null);
    setBanner(null);
    setPending(null);
    setSheet(null);
    setVersion((v) => v + 1);
  };

  const keepScanning = () => {
    const state: RunState = { ...runRef.current, status: "running" };
    runRef.current = state;
    setRun(state);
    setBanner(null);
  };

  const dateSources = (registry.bundle.siteConfig?.reasons.dateSources ?? []).map((r) => ({ value: r.code, label: r.label }));
  const entryValid = pending?.kind === "serial" ? entry.trim().length > 0 : pending?.kind === "quantity" ? Number(entry.replace(",", ".")) > 0 : pending?.kind === "warehouse" ? warehouseChoice !== null : isoDateOk(entry) && entrySource !== null;

  /** "Ta emot" on an entry: the held scan goes on with the value the operator gave. */
  const confirmEntry = async (value: string) => {
    if (!pending) return;
    const p = pending;
    if (p.kind === "warehouse") {
      // The return line gets the chosen warehouse; then the scan goes on as if it had been on the order.
      const pid = p.scan.product?.id;
      if (!pid) return;
      try { await Api.addReceiptLine(receipt.id, { productId: pid, erpWarehouse: warehouseChoice ?? receipt.erpWarehouse ?? null }); onLineAdded(); }
      catch (e) { feedback("rejected"); setBanner({ kind: "bad", label: t("banner.rejected"), message: e instanceof Error ? e.message : String(e) }); return; }
      setPending(null);
      const state = feed(p.scan, { skipEntry: true });
      if (state === "accepted" || state === "partial") setBanner({ kind: "ok", label: t("msg.lineCreated"), message: t("msg.lineCreatedMsg", { what: p.scan.product?.name ?? "", wh: warehouseChoice ?? receipt.erpWarehouse ?? "?" }) });
      return;
    }
    if (p.kind === "serial") {
      const keyed = withSerial(p.scan, value.trim());
      const key = keyed.input.resolved.as === "container" ? keyed.input.resolved.identityKey : null;
      const again = key ? await checkCollision(registry, key, (k, ms) => Api.containersByIdentity(k, ms)) : null;
      if (again) {
        feedback("rejected");
        setBanner({ kind: "bad", label: t("banner.collision"), message: t("msg.serialTaken", { serial: value.trim() }) });
        return;
      }
      setPending(null);
      const state = feed(keyed);
      if (state === "accepted") setBanner({ kind: "ok", label: t("banner.enteredSerial"), message: t("msg.enteredSerial", { what: describeScan(p.scan), serial: value.trim() }) });
    } else if (p.kind === "quantity") {
      const n = Number(value.replace(",", "."));
      if (!(n > 0)) return;
      const std = !p.scan.bulk && p.scan.rules?.capture.lot !== "forbidden" ? p.scan.product?.standardQuantity ?? null : null;
      setPending(null);
      const confirmed = withQuantity(p.scan, String(n), std);
      const state = feed(confirmed, { skipEntry: true });
      if (state === "accepted") setBanner({ kind: "ok", label: t("banner.quantityConfirmed"), message: confirmed.quantity?.source === "standard" ? t("msg.quantityStd", { q: String(n), uom: confirmed.quantity.uom }) : t("msg.quantityEntered", { q: String(n), uom: confirmed.quantity?.uom ?? "", std: std ?? "—" }) });
    } else {
      if (!isoDateOk(value) || !entrySource) return;
      setPending(null);
      const state = feed(withBestBefore(p.scan, value, entrySource), { skipEntry: true });
      if (state === "accepted") setBanner({ kind: "ok", label: t("banner.enteredDate"), message: t("msg.enteredDate", { date: value, source: dateSources.find((d) => d.value === entrySource)?.label ?? entrySource }) });
    }
  };

  /** The escape: reject the container (or count it, per setting), or receive without the date. */
  /** Back out of any entry (owner 2026-11-03: "det behöver man alltid kunna göra"): the scan is dropped, nothing is received. */
  const cancelEntry = () => {
    if (!pending) return;
    const p = pending;
    setPending(null);
    setEntry("");
    setEntrySource(null);
    feedback("rejected");
    setBanner({ kind: "warn", label: t("entry.cancelled"), message: t("entry.cancelledMsg", { what: describeScan(p.scan) }) });
  };
  const escapeEntry = () => {
    if (!pending) return;
    const p = pending;
    setPending(null);
    if (p.kind === "serial") {
      if (cfg?.collisionEscape === "count") {
        feed(p.scan);
        return;
      }
      feedback("rejected");
      setBanner({ kind: "bad", label: t("banner.collision"), message: t("msg.collision", { what: describeScan(p.scan), at: p.at ? t("msg.collisionAt", { code: p.at }) : "" }) });
      return;
    }
    feed(p.scan, { skipEntry: true });
  };

  /** Enter in the entry field: a scanned label yields its serial or date; typed text is taken as is. */
  const onEntryEnter = async () => {
    if (!pending) return;
    let v = entry.trim();
    if (/^\]|[()\u001d]/.test(v)) {
      try {
        const el = await parseScan(v);
        if (pending.kind === "serial" && el.serial) v = el.serial;
        if (pending.kind === "best-before" && (el.bestBefore ?? el.expiry)) v = (el.bestBefore ?? el.expiry)!;
      } catch {
        /* not GS1: keep the text */
      }
    }
    if (pending.kind === "best-before") v = formatIsoDate(v);
    setEntry(v);
    if (pending.kind === "serial" ? v.length > 0 : pending.kind === "quantity" ? Number(v.replace(",", ".")) > 0 : pending.kind === "warehouse" ? warehouseChoice !== null : isoDateOk(v) && entrySource !== null) await confirmEntry(v);
  };

  if (camera) return <CameraView onCode={onCameraCode} onClose={() => setCamera(false)} />;

  const records = [...recordsRef.current.values()];
  const items = records.filter((r) => r.scan.input.resolved.as === "container" && isGoods(r.scan.input.resolved.containerKind));
  const rows = [...tally(records).values()];
  const awaiting = run.status === "awaiting_confirm" && step?.type === "scan_many";
  const isPalletStep = step?.id === "pallet";
  const lineFor = (productId: string | null, gtin: string | null) => receipt.lineItems.find((l) => l.productId !== null && l.productId === productId) ?? receipt.lineItems.find((l) => l.gtin !== null && l.gtin === gtin);
  const receivedSoFar = (lineNo: number, uom: string) => sumSame(progress?.lines.find((x) => x.lineNo === lineNo)?.received ?? [], uom).same;
  // The product number to show: the line's own, else the registry's for the resolved product
  // (a line imported by supplier article carries no sku of its own).
  const skuOf = (l: { sku: string | null; productId: string | null }) => l.sku ?? (l.productId ? registry.bundle.products.find((p) => p.id === l.productId)?.sku : undefined) ?? undefined;

  const contextRows: RowData[] = isPalletStep
    ? receipt.lineItems.slice(0, 3).map((l) => ({ k: l.productName ?? l.description ?? l.sku ?? "", small: skuOf(l), q: `${fmtNum(receivedSoFar(l.lineNo, l.uom))} / ${fmtQty(l.quantity, l.uom)}` }))
    : [
        // The order's lines with what is received so far, this pallet's scans included, and
        // what is left (owner 2026-10-29: step 3 no longer showed the remaining amount).
        ...receipt.lineItems.slice(0, 3).map((l) => {
          const here = rows.find((r) => r.productId !== null && r.productId === l.productId);
          const got = receivedSoFar(l.lineNo, l.uom) + (here?.sums.get(l.uom) ?? 0);
          const left = Math.max(0, Number(l.quantity) - got);
          return { k: l.productName ?? l.description ?? l.sku ?? "", small: [skuOf(l), left > 0 ? t("rows.left", { q: fmtQty(left, l.uom) }) : t("rows.lineDone")].filter(Boolean).join(" · "), q: `${fmtNum(got)} / ${fmtQty(l.quantity, l.uom)}`, tone: left === 0 ? ("ok" as const) : undefined };
        }),
        ...rows.filter((r) => !r.productId || !receipt.lineItems.some((l) => l.productId === r.productId)).slice(0, 2).map((r) => ({ k: r.product, small: t("rows.notOnOrder"), q: [...r.sums].map(([uom, v]) => fmtQty(v, uom)).join(" + ") || t("rows.kolli", { n: r.containers }), tone: "warn" as const })),
      ];
  const firstRow = rows[0];
  const firstLine = firstRow ? lineFor(firstRow.productId, firstRow.gtin) : undefined;
  const contextMore = isPalletStep
    ? receipt.lineItems.length > 3
      ? t("rows.more", { n: receipt.lineItems.length - 3 })
      : undefined
    : receipt.lineItems.length > 3
      ? t("rows.more", { n: receipt.lineItems.length - 3 })
      : undefined;

  const lastLabel: ReactNode = lastScan ? (
    <LabelStrip title={lastScan.input.parsed.sscc ? `${t("unit.pallet")} · ${receipt.supplier?.name ?? receipt.customer?.name ?? ""}` : `${unitWord(lastScan)} · ${lastScan.product?.name ?? t("label.unknownProduct")}`} sub={lastScan.input.parsed.sscc ? t("label.pallet") : t("label.last")} cells={labelCells(lastScan)} />
  ) : (
    <LabelStrip empty={held > 0 ? t("label.held") : t("pallet.none")} />
  );

  return (
    <div className="screen" onClick={() => { unlockAudio(); if (pending) entryRef.current?.focus(); else focusScan(inputRef.current); }}>
      <Strip left={<><b>{receipt.reference}</b> · {receipt.supplier?.name ?? receipt.customer?.name ?? ""}</>} middle={palletSscc ? undefined : location?.code} {...net} />
      {palletSscc && <div className="pallet-no"><span>{t("unit.pallet")}</span><span className="num">(00) {palletSscc.slice(0, -4)}<b>{palletSscc.slice(-4)}</b></span></div>}
      {(receipt.notes?.length ?? 0) > 0 && !pending && <div className="note-strip">{receipt.notes!.map((n, i) => <div key={i}>"{n}"</div>)}</div>}
      <div className="task">
        <div className="task-main">
          {pending?.kind === "warehouse" ? (
            <>
              {banner && <Banner {...banner} />}
              <Prompt step={t("many.step")} title={t("entry.warehouseTitle")} sub={t("entry.warehouseSub", { wh: receipt.erpWarehouse ?? "—" })} />
              <Chips options={warehouseChoices.map((w) => ({ value: w, label: t("entry.warehouse", { n: w }) }))} value={warehouseChoice} onChange={setWarehouseChoice} />
              <LabelStrip title={`${unitWord(pending.scan)} · ${pending.scan.product?.name ?? t("label.unknownProduct")}`} sub={t("label.this")} cells={labelCells(pending.scan)} />
            </>
          ) : pending ? (
            <>
              {banner && <Banner {...banner} />}
              <Prompt step={t("many.step")} title={pending.kind === "serial" ? t("entry.serialTitle") : pending.kind === "quantity" ? t("entry.qtyTitle") : t("entry.dateTitle")} sub={pending.kind === "serial" ? t("entry.serialSub") : pending.kind === "quantity" ? (entrySource === "standard" ? t("entry.qtySubStd") : entrySource === "bulk" ? t("entry.qtySubBulk") : t("entry.qtySub")) : t("entry.dateSub")} />
              <Entry confirm={pending.kind === "quantity" ? { label: entryValid ? t("entry.receiveQty", { q: entry.trim(), uom: unitWord(pending.scan) }) : t("entry.receive"), disabled: !entryValid } : undefined} inputRef={entryRef} value={entry} onChange={(v) => setEntry(pending.kind === "best-before" ? formatIsoDate(v) : v)} onEnter={() => void onEntryEnter()} inputMode={pending.kind === "serial" ? "text" : "numeric"} placeholder={pending.kind === "serial" ? t("entry.serialPlaceholder") : t("entry.dateSub")} tag={pending.kind === "serial" ? "AI 21" : undefined} />
              {pending.kind === "best-before" && (
                <div>
                  <p className="entry-q">{t("entry.dateFrom")}</p>
                  <Chips options={dateSources} value={entrySource} onChange={setEntrySource} />
                </div>
              )}
              <LabelStrip title={`${unitWord(pending.scan)} · ${pending.scan.product?.name ?? t("label.unknownProduct")}`} sub={t("label.this")} cells={labelCells(pending.scan)} />
            </>
          ) : posting ? (
            <Prompt quiet title={t("many.saving")} />
          ) : awaiting ? (
            <>
              <Banner kind="warn" label={t("banner.tally")} message={banner?.message ?? ""} />
              <Chips options={reasonsForShort(registry.bundle.siteConfig ?? null)} value={tallyReason} onChange={setTallyReason} />
            </>
          ) : (
            <Prompt step={isPalletStep ? t("pallet.step") : t("many.step")} title={stepPrompt(run)} sub={isPalletStep ? t("pallet.sub") : undefined}>
              {!isPalletStep && <Glance n={count} label={count === 1 ? t("many.onPalletOne") : t("many.onPallet")} />}
            </Prompt>
          )}
          {!awaiting && !pending && <ScanBar inputRef={inputRef} placeholder={t("scan.ready")} onCamera={() => { unlockAudio(); setCamera(true); }} />}
          {!awaiting && !pending && banner && <Banner {...banner} />}
          {/* On the kolli step the order's lines come first: what is left matters more than the
              last kolli's details, which the banner already gave (owner 2026-10-29). */}
          {!isPalletStep && (contextRows.length > 0 || contextMore) ? <Rows rows={contextRows} more={contextMore} /> : null}
          {!pending && lastLabel}
          {/* Everything scanned on this pallet, newest first, one line each (owner 2026-10-29:
              "see all that is scanned, compact"). */}
          {!isPalletStep && items.length > 0 && (
            <Rows compact title={`${t("side.onPallet")} · ${t("rows.kolli", { n: items.length })}`} rows={items.slice().reverse().map((r) => ({ k: r.scan.product?.name ?? (r.scan.input.parsed.gtin ? `GTIN ${r.scan.input.parsed.gtin}` : t("label.unknownProduct")), small: r.scan.input.parsed.lot ?? r.scan.entered?.serial ?? undefined, q: r.scan.quantity ? fmtQty(r.scan.quantity.value, r.scan.quantity.uom) : "" }))} />
          )}
          {isPalletStep && (contextRows.length > 0 || contextMore) ? <Rows rows={contextRows} more={contextMore} /> : null}
        </div>
        <aside className="task-side">
          {items.length > 0 && (
            <Rows title={t("side.onPallet")} rows={items.slice(-3).reverse().map((r) => ({ k: r.scan.input.parsed.lot ?? r.scan.input.parsed.gtin ?? "", q: r.scan.quantity ? fmtQty(r.scan.quantity.value, r.scan.quantity.uom) : "" }))} more={items.length > 3 ? t("rows.more", { n: items.length - 3 }) : undefined} />
          )}
          <Rows title={t("side.order", { ref: receipt.reference })} rows={receipt.lineItems.map((l) => ({ k: l.productName ?? l.description ?? l.sku ?? "", small: skuOf(l), q: `${fmtNum(receivedSoFar(l.lineNo, l.uom))} / ${fmtQty(l.quantity, l.uom)}` }))} />
          {sessionPallets.length > 0 && <Rows title={t("side.lastPallets")} rows={sessionPallets.map((p) => ({ k: shortSscc(p.sscc), q: `${t("rows.kolli", { n: p.n })} ✓`, tone: "ok" as const }))} />}
        </aside>
      </div>
      <Actions>
        {pending ? (
          <>
            <Btn kind="quiet" icon="back" onClick={cancelEntry}>{t("entry.cancel")}</Btn>
            {pending.kind !== "warehouse" && (pending.kind === "serial" || cfg?.missingRequired === "allow-without") && <Btn kind="secondary" onClick={escapeEntry}>{pending.kind === "serial" ? t("entry.rejectItem") : t("entry.withoutDate")}</Btn>}
            {pending.kind !== "quantity" && <Btn kind="primary" disabled={!entryValid} onClick={() => void confirmEntry(entry)}>{pending.kind === "warehouse" ? t("entry.continue") : t("entry.receive")}</Btn>}
          </>
        ) : awaiting ? (
          <>
            <Btn kind="secondary" onClick={keepScanning}>{t("tally.keep")}</Btn>
            <Btn kind="primary" disabled={!tallyReason} onClick={confirmTally}>{t("tally.confirm")}</Btn>
          </>
        ) : isPalletStep ? (
          <>
            <Btn kind="quiet" icon="back" grow={false} onClick={() => (records.length === 0 ? onAbort() : setSheet("abandon"))} />
            <Btn kind="quiet" onClick={() => setSheet("more")}>{t("common.more")}</Btn>
            <Btn kind="primary" disabled={issuing || posting} onClick={() => void newPallet()}>{issuing ? t("pallet.issuing") : t("pallet.new")}</Btn>
          </>
        ) : (
          <>
            <Btn kind="quiet" icon="back" grow={false} onClick={() => (count === 0 ? restartAtPallet() : setSheet("back"))} />
            <Btn kind="quiet" onClick={() => setSheet("more")}>{t("common.more")}</Btn>
            <Btn kind="primary" disabled={count === 0 || posting} onClick={endPallet}>{t("many.end")}</Btn>
          </>
        )}
      </Actions>

      {sheet === "manual" && (
        <Sheet title={manual ? t("recv.manualFacts", { name: manual.name }) : t("recv.manualWhich")} onClose={() => setSheet(null)}>
          {!manual ? (
            <>
              <p className="hint">{t("recv.manualWhichSub")}</p>
              <div className="list">
                {receipt.lineItems.map((l) => { const lp = l.productId ? registry.bundle.products.find((p) => p.id === l.productId) ?? null : null; const gtin = (registry.bundle.tenantConfig?.erp?.lineGtin ?? "register") === "line" ? l.gtin ?? lp?.gtins[0]?.gtin ?? null : lp?.gtins[0]?.gtin ?? null; /* company setting erp.lineGtin (owner 2026-11-03): the article's GTIN from the register by default, the order line's only when the company says so */ const bulk = !!lp && isBulkProduct(registry, lp); return <ListItem key={l.id} title={l.productName ?? l.description ?? l.sku ?? ""} desc={`${t("recv.line", { n: l.lineNo })} · ${fmtQty(l.quantity, l.uom)}${gtin ? "" : ` · ${t("recv.manualNoGtin")}`}`} off={!gtin} onClick={gtin ? () => setManual({ gtin, bulk, name: l.productName ?? l.sku ?? "", uom: l.uom, lot: l.lot ?? "", bestBefore: l.bestBefore ?? "", qty: "", note: "" }) : undefined} />; })}
              </div>
            </>
          ) : (
            <>
              {!manual.bulk && <Entry value={manual.lot} onChange={(v) => setManual({ ...manual, lot: v })} onEnter={() => undefined} inputMode="text" placeholder={t("recv.manualLot")} />}
              {!manual.bulk && <Entry value={manual.bestBefore} onChange={(v) => setManual({ ...manual, bestBefore: formatIsoDate(v) })} onEnter={() => undefined} inputMode="numeric" placeholder={t("recv.manualBestBefore")} />}
              <Entry value={manual.qty} onChange={(v) => setManual({ ...manual, qty: v })} onEnter={() => undefined} inputMode="numeric" placeholder={t("recv.manualQty", { uom: uomLabel(manual.uom) })} />
              <Entry value={manual.note} onChange={(v) => setManual({ ...manual, note: v })} onEnter={() => undefined} inputMode="text" placeholder={t("recv.manualWhy")} tag={t("common.required")} />
              <p className="hint">{manual.bulk ? t("recv.manualBulkHint") : t("recv.manualHint")}</p>
              <Btn kind="primary" disabled={(!manual.bulk && !manual.lot.trim()) || !manual.qty.trim() || !manual.note.trim()} onClick={() => void manualReceive()}>{t("recv.manualAdd")}</Btn>
            </>
          )}
        </Sheet>
      )}
      {sheet === "more" && (
        <Sheet title={t("more.title")} onClose={() => setSheet(null)}>
          <Btn kind="secondary" icon="camera" onClick={() => { setSheet(null); unlockAudio(); setCamera(true); }}>{t("more.camera")}</Btn>
          {/* Registering without a scan lives under Mer… (owner 2026-11-02: four buttons did not fit the bar). */}
          {canManual(loadCachedBundle()?.siteConfig?.manual, "receive") && <Btn kind="secondary" onClick={() => { setManual(null); setSheet("manual"); }}>{t("recv.manual")}</Btn>}
          {held > 0 && <Btn kind="secondary" onClick={() => void acceptHeldWithoutLot()}>{t("more.acceptWithoutLot")}</Btn>}
          <Btn kind="secondary" onClick={() => setSheet("status")}>{t("more.orderStatus")}</Btn>
          <Btn kind="secondary" disabled={count > 0} onClick={() => { setSheet(null); onCloseDelivery(); }}>{t("more.closeDelivery")}</Btn>
          <Btn kind="danger" onClick={() => (records.length === 0 ? onAbort() : setSheet("abandon"))}>{t("more.abandon")}</Btn>
        </Sheet>
      )}
      {sheet === "status" && (
        <Sheet title={t("more.orderStatus")} onClose={() => setSheet(null)}>
          <ProgressRows receipt={receipt} progress={progress} />
        </Sheet>
      )}
      {sheet === "back" && (
        <Sheet title={t("many.back")} onClose={() => setSheet(null)}>
          <p>{t("many.backConfirm", { n: count })}</p>
          <Btn kind="danger" onClick={restartAtPallet}>{t("many.backYes")}</Btn>
          <Btn kind="secondary" onClick={() => setSheet(null)}>{t("common.cancel")}</Btn>
        </Sheet>
      )}
      {sheet === "abandon" && (
        <Sheet title={t("more.abandon")} onClose={() => setSheet(null)}>
          <p>{t("more.abandonConfirm", { n: records.length })}</p>
          <Btn kind="danger" onClick={onAbort}>{t("more.abandonYes")}</Btn>
          <Btn kind="secondary" onClick={() => setSheet(null)}>{t("common.cancel")}</Btn>
        </Sheet>
      )}
    </div>
  );
}

// --- progress and delivery close --------------------------------------------------

function sumSame(received: Qty[], uom: string): { same: number; other: Qty[] } {
  let same = 0;
  const other: Qty[] = [];
  for (const q of received) {
    if (q.uom === uom) same += Number(q.value);
    else other.push(q);
  }
  return { same, other };
}

function ProgressRows({ receipt, progress }: { receipt: ExpectedReceiptDetail; progress: ReceiptProgress | null }) {
  const rows: RowData[] = receipt.lineItems.map((l) => {
    const p = progress?.lines.find((x) => x.lineNo === l.lineNo);
    const { same, other } = sumSame(p?.received ?? [], l.uom);
    const diff = same - Number(l.quantity);
    return {
      k: l.productName ?? l.description ?? l.sku ?? "",
      small: l.productId === null ? t("pick.unmapped", { n: 1 }) : undefined,
      q: `${fmtNum(same)}${other.length ? ` + ${other.map((q) => fmtQty(q.value, q.uom)).join(" + ")}` : ""} / ${fmtQty(l.quantity, l.uom)}`,
      tone: diff === 0 ? ("ok" as const) : ("warn" as const),
    };
  });
  for (const x of progress?.extra ?? []) {
    rows.push({ k: x.productName ?? `GTIN ${x.gtin ?? "?"}`, small: t("rows.notOnOrder"), q: `+ ${x.received.map((q) => fmtQty(q.value, q.uom)).join(" + ")}`, tone: "warn" });
  }
  return <Rows rows={rows} />;
}

interface Difference {
  key: string;
  text: string;
  lineNo: number | null;
  productId: string | null;
  expected: Qty | null;
  received: Qty[];
  extra: boolean;
}

function CloseDelivery({ receipt, net, queue, siteConfig, onClosed, onBack, needReasons = true }: { /** Returns (owner 2026-11-03): no reason per short line — the difference is shown, the remainder stays open in the ERP. */ needReasons?: boolean; receipt: ExpectedReceiptDetail; net: Net; queue: QueuedStep[]; siteConfig: Registry["bundle"]["siteConfig"] | null; onClosed: (alreadyClosed: boolean) => void; onBack: () => void }) {
  const [progress, setProgress] = useState<ReceiptProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reasons, setReasons] = useState<Record<string, ReasonCode>>({});
  const [other, setOther] = useState<Record<string, string>>({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    Api.progress(receipt.id)
      .then(setProgress)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [receipt.id, queue.length]);

  const differences = useMemo<Difference[]>(() => {
    if (!progress) return [];
    const out: Difference[] = [];
    for (const l of progress.lines) {
      const { same, other: oth } = sumSame(l.received, l.expected.uom);
      if (same !== Number(l.expected.value) || oth.length > 0) {
        out.push({ key: `line-${l.lineNo}`, text: `${t("close.ordered", { q: fmtQty(l.expected.value, l.expected.uom) })}, ${t("close.received", { q: `${fmtNum(same)}${oth.map((q) => ` + ${fmtQty(q.value, q.uom)}`).join("")} ${uomLabel(l.expected.uom)}` })}`, lineNo: l.lineNo, productId: l.productId, expected: l.expected, received: l.received, extra: same > Number(l.expected.value) });
      }
    }
    for (const [i, x] of progress.extra.entries()) {
      out.push({ key: `extra-${i}`, text: t("close.notOnOrder", { q: x.received.map((q) => fmtQty(q.value, q.uom)).join(" + ") }), lineNo: null, productId: x.productId, expected: null, received: x.received, extra: true });
    }
    return out;
  }, [progress]);

  const nameOf = (d: Difference) => (d.lineNo !== null ? progress?.lines.find((l) => l.lineNo === d.lineNo)?.productName ?? progress?.lines.find((l) => l.lineNo === d.lineNo)?.description ?? "" : progress?.extra.find((x) => x.productId === d.productId)?.productName ?? t("label.unknownProduct"));
  const reasonText = (d: Difference) => (reasons[d.key] === "other" ? (other[d.key] ?? "").trim() : reasons[d.key] ?? "");
  const ready = progress !== null && (!needReasons || differences.every((d) => reasonText(d).length > 0)) && queue.length === 0;

  const close = async () => {
    setBusy(true);
    try {
      const s = getSettings();
      const r = await Api.close(receipt.id, {
        differences: differences.map((d) => ({ lineNo: d.lineNo, productId: d.productId, expected: d.expected, received: d.received, reason: needReasons ? reasonText(d) : t("close.returnDiff") })),
        note: note.trim() || null,
        actorId: s.actorId,
        deviceId: s.deviceId,
      });
      onClosed(r.alreadyClosed);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="screen">
      <Strip left={<><b>{receipt.reference}</b> · {receipt.supplier?.name ?? receipt.customer?.name ?? ""}</>} middle={t("more.closeDelivery")} {...net} />
      <div className="body">
        {error && <Banner kind="bad" label={t("banner.rejected")} message={t("close.progressFailed", { err: error })} />}
        {!progress && !error && <p className="hint">{t("common.loading")}</p>}
        {progress && (
          <>
            <Prompt quiet={differences.length === 0} step={t("close.sub", { pallets: progress.pallets, containers: progress.containers })} title={differences.length === 0 ? t("close.none") : differences.length === 1 ? t("close.titleOne") : t("close.title", { n: differences.length })} />
            {differences.map((d) => (
              <div key={d.key} className="field">
                <div className="q"><b>{nameOf(d)}</b> · {d.text}</div>
                {needReasons && <Chips options={d.extra ? reasonsForExtra(siteConfig) : reasonsForShort(siteConfig)} value={reasons[d.key] ?? null} onChange={(v) => setReasons({ ...reasons, [d.key]: v })} />}
                {needReasons && reasons[d.key] === "other" && <input className="input" value={other[d.key] ?? ""} onChange={(e) => setOther({ ...other, [d.key]: e.target.value })} placeholder={t("close.otherPlaceholder")} />}
              </div>
            ))}
            <label className="field">
              <span>{t("close.note")}</span>
              <input className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("close.notePlaceholder")} />
            </label>
            {queue.length > 0 && <p className="hint">{t("close.waitingUpload", { n: queue.length })}</p>}
          </>
        )}
      </div>
      <Actions>
        <Btn kind="quiet" icon="back" onClick={onBack} />
        <Btn kind="primary" disabled={!ready || busy} onClick={() => void close()}>{busy ? t("close.closing") : t("close.submit")}</Btn>
      </Actions>
    </div>
  );
}
